import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { useShowHideIntercomLauncher } from '@hcs/help-chat';
import {
  useComponentDidMount,
  useFixedPortalElement,
  useIsOverflowed,
  useScreenSize,
} from '@hcs/hooks';

import { DirectionalChevron } from '../../../../foundations';
import { TextButton } from '../../../controls-and-inputs';
import { ButtonProps } from '../../../controls-and-inputs/buttons/Button';
import { CloseButton } from '../../../controls-and-inputs/buttons/CloseButton';
import {
  ActionButtonProps,
  ActionButtons,
} from '../../action-buttons/ActionButtons';
import {
  StatusMessage,
  StatusMessageProps,
} from '../../notifications/StatusMessage';

import styles from './Dialog.module.css';

export type DialogTypes =
  | 'normal'
  | 'auto'
  | 'large'
  | 'fullscreen'
  | 'small'
  | 'custom';

export type DialogAction = ButtonProps;

interface DialogTheme {
  Dialog: string;
  Header: string;
  DialogContent: string;
  DialogWrapper: string;
  Actions: string;
  DialogOverlay: string;
}

export interface DialogProps {
  dataHcName: string;
  dataHcEventSection?: string;
  // Children are optional because title, notifications, and actions are enough to render confirmation dialogs
  children?: ReactNode;
  active?: boolean;
  onClose: VoidFunction;
  title?: ReactNode;
  subtitle?: ReactNode;
  theme?: Partial<DialogTheme>;
  actions?: ActionButtonProps['actions'];
  actionsProps?: Omit<ActionButtonProps, 'actions'>;
  width?: number | string;
  maxHeight?: number | string;
  type?: DialogTypes;
  noContentPadding?: boolean;
  preventClickOutsideClose?: boolean;
  titleHeight?: number;
  /*
    forceIsOverflowed:
    the dialog will check if it's content is overflowed and style (drop shadow) accordingly automatically
    but if the content uses something like FlexScroll to handle overflow itself, this is a way to tell Dialog that
    content overflow styles need to be applied
  */
  forceIsOverflowed?: boolean;
  // Whether the dialog should display fullscreen on phone size devices but respect the "type" prop on larger screens
  fullscreenOnPhone?: boolean;
  isOverlayDialog?: boolean;
  actionPortalId?: string;
  notifications?: StatusMessageProps[];
  backButtonText?: string;
}

export const DIALOG_ACTIONS_PORTAL_ID = 'dialog-actions-portal';
export const Dialog = ({
  dataHcName,
  dataHcEventSection,
  theme,
  active,
  title,
  onClose,
  actions,
  actionsProps,
  subtitle,
  children,
  width,
  maxHeight,
  noContentPadding,
  isOverlayDialog,
  type = 'normal',
  preventClickOutsideClose,
  titleHeight,
  forceIsOverflowed = false,
  fullscreenOnPhone,
  actionPortalId = DIALOG_ACTIONS_PORTAL_ID,
  notifications,
  backButtonText = 'Back',
}: DialogProps) => {
  const portalElm = useFixedPortalElement();
  const contentElm = useRef<HTMLDivElement | null>(null);
  const isOverflowed = useIsOverflowed(contentElm, [children]);
  const [activeWithTransition, setActiveWithTransition] = useState(false);
  const { isPhoneWidth } = useScreenSize();

  const effectiveType = fullscreenOnPhone && isPhoneWidth ? 'fullscreen' : type;

  // Hide the Intercom launcher while the dialog is open if the dialog will block the intercom button
  useShowHideIntercomLauncher({
    shouldHide: !!(active && isPhoneWidth && effectiveType === 'fullscreen'),
    hideId: 'dialog',
    errorHandling: false,
  });

  const handleKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setActiveWithTransition(false);
      onClose();
    }
  };
  useComponentDidMount(() => {
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keyup', handleKeyUp);
    };
  });

  useEffect(() => {
    setActiveWithTransition(!!active);
  }, [active]);

  if (!portalElm) return null;

  const dialogStyle: CSSProperties = {};

  if (maxHeight) {
    dialogStyle.maxHeight = maxHeight;
  }
  if (width) {
    dialogStyle.width = width;
  }

  const SubtitleAndNotifications = (
    <>
      {subtitle && <h2>{subtitle}</h2>}
      {notifications && (
        <div>
          {notifications.map((notification) => (
            <StatusMessage key={notification.dataHcName} {...notification} />
          ))}
        </div>
      )}
    </>
  );

  return (
    <>
      {createPortal(
        active && (
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            data-hc-name={`${dataHcName}-wrapper`}
            className={classNames(styles.DialogWrapper, theme?.DialogWrapper, {
              [styles.active]: activeWithTransition,
              [styles.overlayDialog]: isOverlayDialog,
            })}
          >
            <div
              data-hc-name={dataHcName}
              data-hc-event-section={dataHcEventSection}
              className={classNames(
                styles.Dialog,
                theme?.Dialog,
                effectiveType === 'custom' ? undefined : styles[effectiveType],
                {
                  [styles.overflowed]: isOverflowed || forceIsOverflowed,
                  [styles.noPadding]: noContentPadding,
                },
              )}
              style={dialogStyle}
              role="dialog"
            >
              <div
                className={classNames(styles.Header, theme?.Header)}
                style={titleHeight ? { height: `${titleHeight}px` } : {}}
              >
                {effectiveType !== 'fullscreen' && onClose && (
                  <CloseButton
                    dataHcName={`${dataHcName}-closeButton`}
                    className={styles.CloseButton}
                    onClick={onClose}
                  />
                )}
                {effectiveType === 'fullscreen' && onClose && (
                  <TextButton
                    dataHcName={`${dataHcName}-backButton`}
                    className={styles.BackButton}
                    onClick={onClose}
                    icon={
                      <DirectionalChevron
                        dataHcName={`${dataHcName}-DirectionalChevron`}
                        direction="left"
                        size="sm"
                      />
                    }
                  >
                    {backButtonText}
                  </TextButton>
                )}
                {title &&
                  (typeof title === 'string' ? (
                    <h1 data-hc-name={`${dataHcName}-title`}>{title}</h1>
                  ) : (
                    title
                  ))}
                {!isPhoneWidth && SubtitleAndNotifications}
              </div>
              <div
                ref={contentElm}
                data-hc-name={`${dataHcName}-content`}
                className={classNames(
                  theme?.DialogContent,
                  styles.DialogContent,
                )}
              >
                {isPhoneWidth && SubtitleAndNotifications}
                {children}
              </div>
              {/* Used when another component wants to render actions here using a portal */}
              {!actions && actionPortalId && (
                <div
                  id={actionPortalId}
                  className={classNames(theme?.Actions, styles.Actions)}
                />
              )}
              {/* Used when actions are passed directly to this component */}
              {actions && (
                <ActionButtons
                  dataHcName={`${dataHcName}-actions`}
                  className={classNames(theme?.Actions, styles.Actions)}
                  actions={actions}
                  {...actionsProps}
                />
              )}
            </div>
            <div
              data-hc-name={`${dataHcName}-overlay`}
              data-testid="dialog-overlay"
              className={classNames(theme?.DialogOverlay, styles.DialogOverlay)}
              onClick={() => {
                if (!preventClickOutsideClose) {
                  onClose();
                }
              }}
            />
          </div>
        ),
        portalElm,
      )}
    </>
  );
};
